import ContainerForm from 'components/ContainerForm';
import { InfiniteComp } from 'components/infinite-scroll/InfiniteComp';
import { InputTitle } from 'components/input/InputTitle';
import { ConfirmModal } from 'components/modal/ConfirmModal';
import { Checkbox, Spacing, Table, TextInput, Typography, closeModal, openModal } from 'enable-ui';

import { MODAL_KEY } from 'modules/auth/core/modal-key';
import { BorderContainer } from 'modules/dashboard/styles/common';

import { ReactComponent as EmptyLibraryIcon } from 'assets/icons/empty-library-white.svg';
import { ReactComponent as TooltipIcon } from 'assets/icons/tooltip.svg';

import { isEmpty } from 'lodash';

import { COLOR, COLOR_CONFIG } from 'constants/color';

import { AddItemModal } from '../components/AddItemModal';
import { FunctionalTestTable } from '../components/FunctionalTestTable';
import { HeaderDetailPage } from '../components/HeaderDetailPage';
import { getFunctionalTestInAssessmentColumn } from '../core/columns';
import { useAssessmentsFormController } from '../core/controller/assessments-form';

const AddAssessmentPage = () => {
  const {
    assessmentId,
    assessmentDetail,
    goBack,
    values,
    handleChange,
    handleSubmit,
    functionalTestData,
    searchValue,
    onChangeSearchValue,
    selectedData,
    handleAddFunctionalTestToAssessment,
    handleDeleteAssessment,
    handleDeleteFunctionalTestInAssessment,
    onCancel,
    disabled,
    disabledButton,
    onDragEnd,
    onChangeSelectedFunctionalTests,
    selectedFunctionalTests,
    onChangeOnboarding,
    onChangeClass,
    onChangeDefault,
    handleLoadMoreFunctionalTest,
    hasMore,
    isAssessmentDetailEqual,
  } = useAssessmentsFormController();

  const columns = getFunctionalTestInAssessmentColumn(
    onChangeSelectedFunctionalTests,
    selectedFunctionalTests,
  );

  return (
    <ContainerForm
      onCancel={onCancel}
      onSubmit={() => {
        if (
          (assessmentDetail?.isDefault && !values.isDefault) ||
          (assessmentDetail?.isDefault && !values.isOnboarding)
        ) {
          openModal(MODAL_KEY.WARNING_ASSESSMENT);

          return;
        }

        if (values.isDefault && !assessmentDetail?.isDefault) {
          openModal(MODAL_KEY.UPDATE_ASSESSMENT);

          return;
        }

        handleSubmit();
      }}
      disabled={assessmentId ? disabledButton : disabled}
      titleSubmit={assessmentId ? 'Save' : 'Add'}
      titleCancel={assessmentId && 'Cancel changes'}
      disabledCancel={assessmentId ? isAssessmentDetailEqual : false}
    >
      <HeaderDetailPage
        title={assessmentId ? String(assessmentDetail?.name) : 'Add assessment'}
        showAction={Boolean(assessmentId)}
        actions={[
          {
            label: 'Delete',
            onClick: () => openModal(MODAL_KEY.DELETE_ASSESSMENT),
          },
        ]}
        goBack={goBack}
      />
      <BorderContainer spacing="md" placement="bottom" type="margin">
        <InputTitle
          title={
            <Typography height={26} color="#222" weight={500} size={18}>
              Assessment details
            </Typography>
          }
        />

        <Typography
          style={{ margin: '18px 0 6px 0' }}
          size={14}
          weight={600}
          height={20}
          color="#808080"
        >
          Assessment name
        </Typography>

        <TextInput
          name="name"
          containerStyle={{ maxWidth: '800px' }}
          value={values.name}
          onChange={handleChange}
          maxLength={80}
        />

        <Spacing
          style={{ marginTop: 24, marginBottom: values.isOnboarding ? 24 : 0 }}
          spacing="md"
          placement="bottom"
          type="margin"
        >
          <InputTitle
            unsetMarginBottom
            title={
              <Typography weight={600} size={14} height={20} color={COLOR_CONFIG['neutral-text']}>
                Assessment type
              </Typography>
            }
            icon={<TooltipIcon />}
            contentTooltip={
              <div>
                <ul>
                  <li style={{ textAlign: 'left' }}>
                    Baseline fitness test : <br />
                    Select this option to set this assessment as the Onboarding assessment. An
                    Baseline fitness test determines if a client qualifies for a new class. Saving
                    this will replace the existing Onboarding assessment.
                  </li>
                  <li style={{ textAlign: 'left' }}>
                    Class assessment : <br />
                    Select this option if you want to use this assessment to examnine client
                    performance or improvemnent with a class. A class assessment can be added to a
                    course
                  </li>
                </ul>
              </div>
            }
          />
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Checkbox checked={values.isOnboarding} onClick={onChangeOnboarding}>
              <Typography size={16} height={22} color={COLOR.surfaceNeutralBold['neutral-11']}>
                Onboarding assessment
              </Typography>
            </Checkbox>

            <Checkbox checked={values.isClass} onClick={onChangeClass}>
              <Typography size={16} height={22} color={COLOR.surfaceNeutralBold['neutral-11']}>
                Class assessment
              </Typography>
            </Checkbox>
          </div>
        </Spacing>

        {values.isOnboarding && (
          <>
            <InputTitle
              unsetMarginBottom
              title={
                <Typography size={14} height={20} weight={600} color={COLOR_CONFIG['neutral-text']}>
                  Default assessment (optional)
                </Typography>
              }
            />

            <Checkbox checked={values.isDefault} onClick={onChangeDefault}>
              <Typography size={16} height={22} color={COLOR.surfaceNeutralBold['neutral-11']}>
                Set as default baseline fitness test. Only this baseline fitness test can be used on
                the mobile app.
              </Typography>
            </Checkbox>
          </>
        )}
      </BorderContainer>

      <BorderContainer className="mt-24" spacing="md" placement="bottom" type="margin">
        <InputTitle
          title={
            <Typography height={26} color="#222" weight={500} size={18}>
              Functional tests
            </Typography>
          }
        />

        <>
          {!isEmpty(selectedData) ? (
            <FunctionalTestTable
              functionalTests={selectedData}
              onDelete={handleDeleteFunctionalTestInAssessment}
              onDragEnd={onDragEnd}
            />
          ) : (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexDirection: 'column',
              }}
            >
              <EmptyLibraryIcon />
              <Typography size={14} height={20} color={COLOR_CONFIG['neutral-text']}>
                No functional test was addedd yet
              </Typography>
            </div>
          )}

          <AddItemModal
            title="Add test"
            selectedData={selectedData}
            onAdd={handleAddFunctionalTestToAssessment}
            placeholderSearch="Search by test"
            options={functionalTestData}
            isShowAddFormButton={true}
            formMode={assessmentId && !isEmpty(selectedData) ? 'edit' : 'add'}
            searchValue={searchValue.value}
            onChange={onChangeSearchValue}
            content={
              <InfiniteComp hasMore={hasMore.value} handleLoadMore={handleLoadMoreFunctionalTest}>
                <Table
                  columns={columns}
                  dataSource={functionalTestData}
                  total={99999}
                  tableKey={''}
                  queryState={{
                    page: 1,
                    limit: 99999,
                  }}
                  paginationConfigs={{
                    show: false,
                  }}
                  headerConfigs={{
                    show: false,
                  }}
                />
              </InfiniteComp>
            }
          />

          {isEmpty(selectedData) && (
            <Typography
              color="#FF4D4F"
              style={{
                marginTop: 8,
                textAlign: 'center',
              }}
            >
              Add at least 1 functional test to save.
            </Typography>
          )}
        </>
      </BorderContainer>

      <ConfirmModal
        modalKey={MODAL_KEY.DELETE_ASSESSMENT}
        content={'Delete these assesement.'}
        header={`Delete assessment`}
        onCancel={() => closeModal(MODAL_KEY.DELETE_ASSESSMENT)}
        onSubmit={handleDeleteAssessment}
      />

      <ConfirmModal
        modalKey={MODAL_KEY.WARNING_ASSESSMENT}
        content={
          'Without default baseline fitness test, Trainers will be unable to start baseline fitness tests on the mobile app.'
        }
        header={'Remove default baseline fitness test'}
        onCancel={() => closeModal(MODAL_KEY.WARNING_ASSESSMENT)}
        onSubmit={handleSubmit}
        submitButton={{
          label: 'Save',
          buttonProps: { style: { background: '#FD8A24' } },
        }}
      />

      <ConfirmModal
        modalKey={MODAL_KEY.UPDATE_ASSESSMENT}
        content={
          'This will replace the current default baseline fitness test with this assessment. '
        }
        header={'Save as default baseline fitness test'}
        onCancel={() => closeModal(MODAL_KEY.UPDATE_ASSESSMENT)}
        onSubmit={handleSubmit}
        submitButton={{
          label: 'Save',
          buttonProps: { style: { background: '#FD8A24' } },
        }}
      />
    </ContainerForm>
  );
};

export default AddAssessmentPage;
